import { z } from 'zod'

/**
 * Reports over the read model (`metric_rollups`): one number per metric, day
 * and campus. Every metric here is a sum, so any interval is a sum of days —
 * which is why "unique givers this month" is not a metric: uniques do not add.
 *
 * Pure: the report builder's preview, the dashboard, the scheduled email and
 * the export all aggregate with these functions.
 */

export const METRICS = {
  service_attendance: { label: 'Service attendance', unit: 'count', permission: 'attendance:read' },
  checkins: { label: 'Children checked in', unit: 'count', permission: 'checkin:read' },
  group_attendance: { label: 'Group attendance', unit: 'count', permission: 'attendance:read' },
  group_joins: { label: 'People joining groups', unit: 'count', permission: 'groups:read' },
  new_people: { label: 'New people', unit: 'count', permission: 'people:read' },
  giving_cents: { label: 'Giving', unit: 'cents', permission: 'giving:read' },
  gifts: { label: 'Gifts', unit: 'count', permission: 'giving:read' },
} as const

export type Metric = keyof typeof METRICS
export const METRIC_IDS = Object.keys(METRICS) as Metric[]

export const reportDefinitionSchema = z.object({
  metrics: z
    .array(z.enum(METRIC_IDS as [Metric, ...Metric[]]))
    .min(1)
    .max(6),
  interval: z.enum(['day', 'week', 'month']),
  range: z.discriminatedUnion('kind', [
    z.object({
      kind: z.literal('last'),
      count: z.number().int().min(1).max(104),
      unit: z.enum(['weeks', 'months']),
    }),
    z.object({
      kind: z.literal('between'),
      from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      to: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    }),
  ]),
  /** null ⇒ the whole church. */
  campusId: z.uuid().nullable().default(null),
})
export type ReportDefinition = z.infer<typeof reportDefinitionSchema>

const DAY = 86_400_000
const toDate = (iso: string) => new Date(`${iso}T00:00:00Z`)
const iso = (date: Date) => date.toISOString().slice(0, 10)

/** Monday of the ISO week containing `day`. */
export function weekStart(day: string): string {
  const d = toDate(day)
  const offset = (d.getUTCDay() + 6) % 7
  return iso(new Date(d.getTime() - offset * DAY))
}

export const monthStart = (day: string) => `${day.slice(0, 7)}-01`

export function bucketOf(day: string, interval: ReportDefinition['interval']): string {
  return interval === 'day' ? day : interval === 'week' ? weekStart(day) : monthStart(day)
}

/** The inclusive dates a definition covers, aligned to whole buckets. */
export function resolveRange(
  def: Pick<ReportDefinition, 'range' | 'interval'>,
  today: string,
): { from: string; to: string } {
  if (def.range.kind === 'between') {
    return def.range.from <= def.range.to
      ? { from: def.range.from, to: def.range.to }
      : { from: def.range.to, to: def.range.from }
  }
  if (def.range.unit === 'weeks') {
    const thisWeek = weekStart(today)
    return {
      from: iso(new Date(toDate(thisWeek).getTime() - (def.range.count - 1) * 7 * DAY)),
      to: today,
    }
  }
  const d = toDate(monthStart(today))
  d.setUTCMonth(d.getUTCMonth() - (def.range.count - 1))
  return { from: iso(d), to: today }
}

/** Every bucket from `from` to `to`, so a quiet week shows as 0, not as a gap. */
export function buckets(
  from: string,
  to: string,
  interval: ReportDefinition['interval'],
): string[] {
  const out: string[] = []
  let cursor = bucketOf(from, interval)
  while (cursor <= to) {
    out.push(cursor)
    const d = toDate(cursor)
    if (interval === 'day') d.setUTCDate(d.getUTCDate() + 1)
    else if (interval === 'week') d.setUTCDate(d.getUTCDate() + 7)
    else d.setUTCMonth(d.getUTCMonth() + 1)
    cursor = iso(d)
  }
  return out
}

export interface RollupRow {
  metric: string
  day: string
  value: number
}

export interface ReportTable {
  from: string
  to: string
  interval: ReportDefinition['interval']
  metrics: Metric[]
  rows: { bucket: string; values: Record<string, number> }[]
  totals: Record<string, number>
}

export function aggregate(
  def: ReportDefinition,
  rows: readonly RollupRow[],
  today: string,
): ReportTable {
  const { from, to } = resolveRange(def, today)
  const keys = buckets(from, to, def.interval)
  const index = new Map(keys.map((key, i) => [key, i]))
  const table = keys.map((bucket) => ({
    bucket,
    values: Object.fromEntries(def.metrics.map((m) => [m, 0])) as Record<string, number>,
  }))
  const totals = Object.fromEntries(def.metrics.map((m) => [m, 0])) as Record<string, number>
  for (const row of rows) {
    if (row.day < from || row.day > to || !(def.metrics as string[]).includes(row.metric)) continue
    const i = index.get(bucketOf(row.day, def.interval))
    const target = i === undefined ? undefined : table[i]
    if (!target) continue
    target.values[row.metric] = (target.values[row.metric] ?? 0) + row.value
    totals[row.metric] = (totals[row.metric] ?? 0) + row.value
  }
  return { from, to, interval: def.interval, metrics: def.metrics, rows: table, totals }
}

/** "Week of 6 Oct", "Oct 2026", "Tue 7 Oct" — the row label a person reads. */
export function bucketLabel(bucket: string, interval: ReportDefinition['interval']): string {
  const d = toDate(bucket)
  if (interval === 'month')
    return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric', timeZone: 'UTC' })
  const short = d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', timeZone: 'UTC' })
  return interval === 'week'
    ? `Week of ${short}`
    : d.toLocaleDateString('en-US', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        timeZone: 'UTC',
      })
}

/** Percentage change from `previous` to `current`, or null when there is no base. */
export function change(current: number, previous: number): number | null {
  if (previous === 0) return null
  return Math.round(((current - previous) / previous) * 100)
}

/** The report as rows of strings — the shared shape of CSV, XLSX and PDF. */
export function reportGrid(table: ReportTable, formatCents: (cents: number) => string): string[][] {
  const header = ['Period', ...table.metrics.map((m) => METRICS[m].label)]
  const fmt = (metric: Metric, value: number) =>
    METRICS[metric].unit === 'cents' ? formatCents(value) : String(value)
  const body = table.rows.map((row) => [
    bucketLabel(row.bucket, table.interval),
    ...table.metrics.map((m) => fmt(m, row.values[m] ?? 0)),
  ])
  const total = ['Total', ...table.metrics.map((m) => fmt(m, table.totals[m] ?? 0))]
  return [header, ...body, total]
}

/** When a scheduled report next goes out: Monday 7am (weekly), the 1st at 7am (monthly), UTC. */
export function nextRun(schedule: 'weekly' | 'monthly', after: Date): Date {
  const d = new Date(Date.UTC(after.getUTCFullYear(), after.getUTCMonth(), after.getUTCDate(), 7))
  if (schedule === 'weekly') {
    const daysToMonday = (8 - d.getUTCDay()) % 7 || 7
    d.setUTCDate(d.getUTCDate() + daysToMonday)
    return d
  }
  d.setUTCDate(1)
  d.setUTCMonth(d.getUTCMonth() + 1)
  return d
}
